/**
 * Grouping the Trainings list: RELEASE (10.2) → VERSION (10.2.0.27) →
 * trainings, newest release first. Pure, so the page's shape is proven
 * without mounting it.
 *
 * A training is pinned to ONE release row (`default_release`). When that row
 * is a version under a release, portal-api also sends `release_line`; when it
 * is the release itself, there is no `release_line` and the training belongs
 * to the whole release ("All versions"). An older payload without
 * `release_line` falls back to the version's first two segments, which is
 * the same `line_key` portal-api files a version under.
 */
import { compareVersionsDesc } from "../releases/api.js";
import type { DocSummary } from "../../knowledge-base/types.js";

export interface TrainingVersionGroup {
  /** The version string, or `null` for trainings pinned to the release
   *  itself (they apply to every version in it). */
  version: string | null;
  trainings: DocSummary[];
}

export interface TrainingReleaseGroup {
  /** "10.2" — or "Other" for a training pinned to nothing (not expected;
   *  portal-api refuses a training without a release). */
  release: string;
  versions: TrainingVersionGroup[];
  count: number;
}

const OTHER = "Other";

/** The release a version is filed under, from its own number: 10.2.0.27 → 10.2. */
export function lineKeyOf(version: string): string {
  return version.split(".").slice(0, 2).join(".");
}

function releaseOf(t: DocSummary): { release: string; version: string | null } {
  const pinned = t.default_release;
  if (!pinned) return { release: OTHER, version: null };
  if (t.release_line) return { release: t.release_line.version, version: pinned.version };
  // No line sent: either the training hangs off the release itself (10.2),
  // or an older portal-api did not say. Two segments or fewer IS a release.
  const key = lineKeyOf(pinned.version);
  return key === pinned.version
    ? { release: pinned.version, version: null }
    : { release: key, version: pinned.version };
}

/** A human label for what a training is. An uploaded PDF is stored as a
 *  `guide`, a video (uploaded or linked) as `video`. */
export function trainingKind(t: Pick<DocSummary, "doc_type">): string {
  switch (t.doc_type) {
    case "video":
      return "Video";
    case "guide":
    case "file":
      return "PDF";
    case "faq":
      return "FAQ";
    case "slide":
      return "Slides";
    default:
      return "Article";
  }
}

/** Case-insensitive match of every word in `q` against title + summary. */
export function searchTrainings(trainings: readonly DocSummary[], q: string): DocSummary[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...trainings];
  return trainings.filter((t) => {
    const hay = `${t.title} ${t.summary ?? ""}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

export function groupTrainings(trainings: readonly DocSummary[]): TrainingReleaseGroup[] {
  const byRelease = new Map<string, Map<string | null, DocSummary[]>>();
  for (const t of trainings) {
    const { release, version } = releaseOf(t);
    const versions = byRelease.get(release) ?? new Map<string | null, DocSummary[]>();
    byRelease.set(release, versions);
    const list = versions.get(version) ?? [];
    list.push(t);
    versions.set(version, list);
  }

  const byTitle = (a: DocSummary, b: DocSummary): number => a.title.localeCompare(b.title);
  const groups: TrainingReleaseGroup[] = [...byRelease.entries()].map(([release, versions]) => {
    const vs: TrainingVersionGroup[] = [...versions.entries()]
      .map(([version, list]) => ({ version, trainings: [...list].sort(byTitle) }))
      // Release-wide trainings first, then versions newest first.
      .sort((a, b) =>
        a.version === null ? -1 : b.version === null ? 1 : compareVersionsDesc(a.version, b.version),
      );
    return { release, versions: vs, count: vs.reduce((n, v) => n + v.trainings.length, 0) };
  });

  return groups.sort((a, b) =>
    a.release === OTHER ? 1 : b.release === OTHER ? -1 : compareVersionsDesc(a.release, b.release),
  );
}
